import {
  Link as MaterialLink,
  makeStyles,
  CircularProgress,
  Button as MaterialButton,
  Checkbox,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import cn from 'classnames';
import { format } from 'date-fns';
import { useCallback, useContext, Dispatch, SetStateAction } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Column, Table } from 'react-virtualized';
import { Goal, GoalId, GoalStatus } from '../../api/types/Goal';
import { Role } from '../../api/user/Role';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import {
  checkSizeTablet,
  encodeQuery,
  getColorGoalStatus,
  isMobile,
  isTablet,
} from '../../utils/functions';
import { GoalSlider, StatusBadge, Text, AlertState } from '../common';
import {
  TableCell,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  TableFooter,
} from '../common/table';
import GoalsRowActions from '../goals/goals-row-actions';

const useStyles = makeStyles({
  container: {
    padding: 0,
  },
  table: {
    outline: 'none',
  },
  column: {
    display: 'flex',
  },
  nameColumn: {
    paddingLeft: 16,
    paddingRight: 10,
    boxSizing: 'border-box',
  },
  progressColumn: {
    width: '100%',
    paddingRight: 20,
    paddingLeft: 20,
    boxSizing: 'border-box',
  },
  startDateColumn: {
    paddingRight: 20,
    boxSizing: 'border-box',
  },
  completionDateColumn: {
    paddingRight: 20,
    boxSizing: 'border-box',
  },
  ventureColumn: {
    paddingRight: 20,
    paddingLeft: 10,
    boxSizing: 'border-box',
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 500,
  },
  emptyStateBtn: {
    marginTop: 16,
  },
});

export interface GoalsTableBasicProps {
  goals: Goal[];
  page: number;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  isLoading: boolean;
  hasError?: string;
  hideTopBorder?: boolean;
  venture?: { id: string; ventureName: string };
  showVentures?: boolean;
  selectedGoalIds?: GoalId[];
  setSelectedGoalIds?: Dispatch<SetStateAction<GoalId[]>>;
  onPageChange: (page: number) => void;
  onGoalSliderChange: (args: {
    id: GoalId;
    progress: number;
    status: GoalStatus;
  }) => void;
  onGoalUpdate?: (updatedGoal: Goal) => void;
}

export default function GoalsTableBasic({
  goals,
  page,
  hasNextPage,
  isNextPageLoading,
  isLoading,
  hasError,
  hideTopBorder,
  venture,
  showVentures,
  selectedGoalIds,
  setSelectedGoalIds,
  onPageChange,
  onGoalSliderChange,
  onGoalUpdate,
}: GoalsTableBasicProps) {
  const classes = useStyles();
  const location = useLocation();
  const { hasAccessToAction, hasRole } = useContext(UserContext);
  const currentPath = `${location.pathname}${location.search}`;
  const { width: containerWidth, ref: containerRef } = useResizeDetector();

  const canCreateNewGoal = hasAccessToAction('goal.create');
  const canCreateCheckin = hasAccessToAction('goal.checkins.create');
  const showCheckboxes =
    canCreateCheckin &&
    selectedGoalIds !== undefined &&
    setSelectedGoalIds !== undefined;

  const stopPropagation = (e: any) => e.stopPropagation();

  const formatGoalDate = (stringDate: string | null) => {
    if (!stringDate) return '-';
    const date = new Date(stringDate);
    return format(date, 'LLL dd, y');
  };

  const handleAllRowSelect = useCallback(
    (checked: boolean) => {
      if (!setSelectedGoalIds) return;
      if (checked) {
        const goalIds = goals.map((goal) => goal.id);
        setSelectedGoalIds(goalIds);
      } else {
        setSelectedGoalIds([]);
      }
    },
    [goals, setSelectedGoalIds],
  );

  const handleRowSelect = useCallback(
    (checked: boolean, goalId: GoalId) => {
      if (!setSelectedGoalIds) return;
      if (checked) {
        setSelectedGoalIds((prev) => [...prev, goalId]);
      } else {
        setSelectedGoalIds((prev) => prev.filter((id) => id !== goalId));
      }
    },
    [setSelectedGoalIds],
  );

  const handleGoalUpdate = useCallback(
    (updatedGoal: Goal) => {
      onGoalSliderChange({
        id: updatedGoal.id,
        progress: updatedGoal.progress,
        status: updatedGoal.status,
      });
      if (onGoalUpdate) {
        onGoalUpdate(updatedGoal);
      }
    },
    [onGoalSliderChange, onGoalUpdate],
  );

  const isXSmallDesktop = checkSizeTablet(1260);

  return (
    <div ref={containerRef}>
      {isLoading ? (
        <div className={classes.emptyBlock}>
          <CircularProgress size={36} color='primary' />
        </div>
      ) : hasError ? (
        <div className={classes.emptyBlock}>
          <AlertState type='error'>{hasError}</AlertState>
        </div>
      ) : goals.length === 0 ? (
        <div className={classes.emptyBlock}>
          <div className={classes.emptyState}>
            <Text variant='normal'>
              We cannot find any goals based on selected criteria.
              {!hasRole(Role.Admin) && !hasRole(Role.Manager) ? (
                <>
                  <br />
                  If this is unexpected, please reach out to the Administrator
                  to verify.
                </>
              ) : null}
              <br />
              Feel free to create a new goal!
            </Text>
            {canCreateNewGoal && (
              <MaterialButton
                component={RouterLink}
                to={{
                  pathname: Pages.NEW_GOAL,
                  state: { prevPath: currentPath },
                  search: venture
                    ? `?venture=${encodeQuery({
                      id: venture.id,
                      ventureName: venture.ventureName,
                    })}`
                    : '',
                }}
                className={classes.emptyStateBtn}
                variant='contained'
                color='primary'
                startIcon={<AddIcon />}>
                Goal
              </MaterialButton>
            )}
          </div>
        </div>
      ) : (
        <>
          <Table
            gridClassName={classes.table}
            headerHeight={56}
            height={64 * (goals.length > 10 ? goals.length : 10) + 56}
            rowHeight={64}
            rowCount={goals.length}
            rowGetter={({ index }) => goals[index]}
            headerRowRenderer={(headRowProps) => (
              <TableHeadRow {...headRowProps} hideTopBorder={hideTopBorder} />
            )}
            rowRenderer={(rowProps) => (
              <TableRow
                data-testid={`goal-table-row-${rowProps.index}`}
                selected={
                  selectedGoalIds?.includes(rowProps.rowData.id) || false
                }
                {...rowProps}
              />
            )}
            width={containerWidth || 800}>
            {showCheckboxes && (
              <Column
                dataKey='id'
                className={classes.column}
                width={!isTablet() ? 72 : 30}
                minWidth={!isTablet() ? 72 : 30}
                headerRenderer={() => (
                  <div>
                    {!isTablet() && (
                      <Checkbox
                        data-testid='goal-table-row-checkbox'
                        checked={
                          (selectedGoalIds?.length || 0) === goals.length
                        }
                        color='primary'
                        indeterminate={
                          (selectedGoalIds?.length || 0) > 0 &&
                          (selectedGoalIds?.length || 0) !== goals.length
                        }
                        onChange={(e) => handleAllRowSelect(e.target.checked)}
                      />
                    )}
                  </div>
                )}
                cellRenderer={({ cellData }) => (
                  <div>
                    {!isTablet() && (
                      <Checkbox
                        checked={selectedGoalIds?.includes(cellData) || false}
                        color='primary'
                        onChange={(e) =>
                          handleRowSelect(e.target.checked, cellData)
                        }
                        onClick={stopPropagation}
                      />
                    )}
                  </div>
                )}
              />
            )}
            <Column
              dataKey='title'
              className={cn(classes.column, classes.nameColumn)}
              headerClassName={classes.nameColumn}
              width={172}
              minWidth={isMobile() ? 120 : 172}
              maxWidth={357}
              flexGrow={1}
              headerRenderer={() => <TableHeadCell>Name</TableHeadCell>}
              cellRenderer={({ cellData, rowIndex, rowData }) => (
                <TableCell truncated>
                  <MaterialLink
                    onClick={stopPropagation}
                    component={RouterLink}
                    to={{
                      pathname: getRoutePath(Pages.GOAL_OVERVIEW, {
                        goalId: rowData.id,
                      }),
                      state: { prevPath: currentPath },
                    }}>
                    {cellData}
                  </MaterialLink>
                </TableCell>
              )}
            />
            {showVentures && !isMobile() && (
              <Column
                dataKey='ventureName'
                className={cn(classes.column, classes.ventureColumn)}
                headerClassName={classes.ventureColumn}
                width={240}
                minWidth={180}
                maxWidth={320}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Venture</TableHeadCell>}
                cellRenderer={({ cellData, rowData }) => (
                  <TableCell truncated onClick={stopPropagation}>
                    <MaterialLink
                      onClick={stopPropagation}
                      component={RouterLink}
                      to={{
                        pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                          ventureId: rowData.ventureId,
                        }),
                        state: {
                          prevPath: currentPath,
                        },
                      }}>
                      {cellData}
                    </MaterialLink>
                  </TableCell>
                )}
              />
            )}
            <Column
              dataKey='status'
              className={classes.column}
              width={100}
              minWidth={100}
              maxWidth={150}
              flexGrow={1}
              headerRenderer={() => <TableHeadCell>Status</TableHeadCell>}
              cellRenderer={({ cellData }) => {
                const currentVariant = getColorGoalStatus(cellData);
                const readableStatus = cellData
                  ? cellData.replace(/_/g, ' ')
                  : '';
                return (
                  <StatusBadge
                    status={readableStatus}
                    variant={currentVariant}
                  />
                );
              }}
            />
            {!isTablet() && (
              <Column
                dataKey='progress'
                className={cn(classes.column, classes.progressColumn)}
                headerClassName={classes.progressColumn}
                width={256}
                minWidth={256}
                maxWidth={415}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Progress</TableHeadCell>}
                cellRenderer={({ rowData }) => (
                  <div style={{ flex: 1 }}>
                    <GoalSlider goal={rowData} onChange={onGoalSliderChange} />
                  </div>
                )}
              />
            )}
            {!isXSmallDesktop && (
              <Column
                dataKey='startDate'
                className={cn(classes.column, classes.startDateColumn)}
                headerClassName={classes.startDateColumn}
                width={140}
                minWidth={140}
                maxWidth={200}
                flexGrow={1}
                headerRenderer={() => <TableHeadCell>Start Date</TableHeadCell>}
                cellRenderer={({ cellData }) => (
                  <TableCell onClick={stopPropagation} truncated>
                    {formatGoalDate(cellData)}
                  </TableCell>
                )}
              />
            )}
            {!isXSmallDesktop && (
              <Column
                dataKey='targetDate'
                className={cn(classes.column, classes.completionDateColumn)}
                headerClassName={classes.completionDateColumn}
                width={124}
                minWidth={124}
                maxWidth={205}
                flexGrow={1}
                headerRenderer={() => (
                  <TableHeadCell>Completion Date</TableHeadCell>
                )}
                cellRenderer={({ cellData }) => (
                  <TableCell onClick={stopPropagation} truncated>
                    {formatGoalDate(cellData)}
                  </TableCell>
                )}
              />
            )}
            <Column
              dataKey='actions'
              className={classes.column}
              width={72}
              minWidth={72}
              maxWidth={72}
              headerRenderer={() => <div></div>}
              cellRenderer={({ rowData }) => (
                <TableCell onClick={stopPropagation}>
                  <GoalsRowActions
                    goal={rowData}
                    variant='table'
                    onGoalUpdate={handleGoalUpdate}
                  />
                </TableCell>
              )}
            />
          </Table>
          <TableFooter
            page={page}
            onPageChange={onPageChange}
            disabled={isLoading}
            isLoading={isNextPageLoading}
            hasNextPage={goals.length > 0 && hasNextPage}
          />
        </>
      )}
    </div>
  );
}
